<template>
  <div class="min-h-screen bg-bochas-bg px-4 py-6 sm:py-8">
    <div class="max-w-7xl mx-auto">
      <!-- Header -->
      <div
        class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6 sm:mb-8"
      >
        <div>
          <h1 class="text-2xl sm:text-3xl font-bold text-white">
            Categorías de torneos
          </h1>
          <p class="mt-1 text-sm text-gray-400">
            Administrá las categorías disponibles para torneos y resultados.
          </p>
        </div>
        <div class="flex flex-wrap items-center gap-2">
          <router-link to="/admin/resultados" class="btn-bochas-trans text-sm">
            ← Resultados
          </router-link>
          <button
            type="button"
            class="btn-bochas-rojo text-sm"
            @click="openModalCreate"
          >
            + Nueva categoría
          </button>
        </div>
      </div>

      <!-- Buscador -->
      <div class="mb-4">
        <input
          v-model="search"
          type="text"
          placeholder="Buscar categoría por nombre..."
          class="w-full sm:max-w-md px-4 py-2.5 bg-bochas-bg border border-gray-700 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-bochas-rojo transition-colors"
        />
      </div>

      <!-- Loading -->
      <div v-if="loading" class="text-center py-12">
        <div
          class="inline-block w-10 h-10 sm:w-12 sm:h-12 border-4 border-bochas-rojo border-t-transparent rounded-full animate-spin"
        ></div>
      </div>

      <!-- Error -->
      <div
        v-else-if="error"
        class="bg-bochas-rojo/20 border border-bochas-rojo text-red-200 rounded-lg p-4 mb-4"
      >
        {{ error }}
      </div>

      <!-- Tabla de categorías -->
      <div
        v-else-if="filteredCategorias.length === 0"
        class="card-fbochas p-8 text-center"
      >
        <p class="text-gray-400">No se encontraron categorías.</p>
      </div>

      <div v-else class="card-fbochas overflow-hidden">
        <div class="overflow-x-auto">
          <table class="w-full text-left text-sm text-gray-300">
            <thead
              class="bg-white/5 border-b border-white/10 text-xs uppercase tracking-wider text-gray-400"
            >
              <tr>
                <th class="px-4 py-3 w-16 text-center">#</th>
                <th class="px-4 py-3">Nombre</th>
                <th class="px-4 py-3 w-28 text-center">Orden</th>
                <th class="px-4 py-3 w-40 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-white/10">
              <tr
                v-for="(cat, idx) in filteredCategorias"
                :key="cat.id || idx"
                class="hover:bg-white/[0.02] transition-colors"
              >
                <td
                  class="px-4 py-3 text-center text-gray-500 font-mono text-xs"
                >
                  {{ idx + 1 }}
                </td>
                <td class="px-4 py-3 font-medium text-white">
                  {{ cat.name }}
                </td>
                <td class="px-4 py-3 text-center text-gray-400">
                  {{ cat.display_order ?? 0 }}
                </td>
                <td class="px-4 py-3 text-right space-x-2">
                  <button
                    type="button"
                    class="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border transition-colors border-sky-400/25 bg-sky-500/10 text-sky-200 hover:bg-sky-500/20"
                    @click="openModalEdit(cat)"
                   title="Editar" aria-label="Editar"><IconFbochas name="edit" :size="16" /></button>
                  <button
                    type="button"
                    class="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border transition-colors border-red-400/25 bg-red-500/10 text-red-300 hover:bg-red-500/20"
                    @click="onDelete(cat)"
                   title="Eliminar" aria-label="Eliminar"><IconFbochas name="trash" :size="16" /></button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Modal Crear / Editar -->
      <div
        v-if="modalOpen"
        class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
        @click.self="modalOpen = false"
      >
        <div
          class="card-fbochas w-full max-w-md p-6 space-y-4 shadow-2xl border border-white/10"
        >
          <div
            class="flex items-center justify-between border-b border-white/10 pb-3"
          >
            <h2 class="text-lg font-bold text-white">
              {{ isEditing ? "Editar categoría" : "Nueva categoría" }}
            </h2>
            <button
              type="button"
              class="text-gray-400 hover:text-white"
              @click="modalOpen = false"
            >
              ✕
            </button>
          </div>

          <form @submit.prevent="onSave" class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-gray-300 mb-1">
                Nombre de la categoría *
              </label>
              <input
                v-model="modalForm.name"
                type="text"
                required
                placeholder="Ej: Parejas 2° Categoría"
                class="w-full px-3 py-2 bg-bochas-bg border border-gray-700 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-bochas-rojo transition-colors"
              />
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-300 mb-1">
                Orden de visualización
              </label>
              <input
                v-model.number="modalForm.display_order"
                type="number"
                min="0"
                placeholder="0"
                class="w-full px-3 py-2 bg-bochas-bg border border-gray-700 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-bochas-rojo transition-colors"
              />
              <p class="text-xs text-gray-500 mt-1">
                Define la prioridad al listarse en los selectores.
              </p>
            </div>

            <div
              v-if="modalError"
              class="bg-bochas-rojo/20 border border-bochas-rojo text-red-200 text-xs rounded p-2.5"
            >
              {{ modalError }}
            </div>

            <div class="flex justify-end gap-2 pt-2">
              <button
                type="button"
                class="btn-bochas-trans text-sm px-4 py-2"
                @click="modalOpen = false"
              >
                Cancelar
              </button>
              <button
                type="submit"
                :disabled="modalSaving"
                class="btn-bochas-rojo text-sm px-4 py-2"
                :class="{ 'opacity-60 cursor-not-allowed': modalSaving }"
              >
                {{
                  modalSaving
                    ? "Guardando..."
                    : isEditing
                    ? "Guardar cambios"
                    : "Crear categoría"
                }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import IconFbochas from "@/components/Icons/IconFbochas.vue";
import { ref, computed, reactive, onMounted } from "vue";
import {
  listarCategoriasCatalogo,
  crearCategoriaCatalogo,
  editarCategoriaCatalogo,
  eliminarCategoriaCatalogo,
} from "@/services/tournaments/tournamentsService";
import { useConfirm } from "@/composables/useConfirm";

const { confirm } = useConfirm();
const loading = ref(false);
const error = ref("");
const search = ref("");
const categorias = ref([]);

const modalOpen = ref(false);
const modalSaving = ref(false);
const modalError = ref("");
const isEditing = ref(false);
const editingId = ref(null);

const modalForm = reactive({
  name: "",
  display_order: 0,
});

const filteredCategorias = computed(() => {
  const q = search.value.trim().toLowerCase();
  if (!q) return categorias.value;
  return categorias.value.filter((cat) =>
    (cat.name || "").toLowerCase().includes(q),
  );
});

async function loadData() {
  loading.value = true;
  error.value = "";
  try {
    const data = await listarCategoriasCatalogo();
    categorias.value = Array.isArray(data) ? data : data.items || [];
  } catch (err) {
    error.value =
      err.response?.data?.message || "No se pudieron cargar las categorías.";
  } finally {
    loading.value = false;
  }
}

function openModalCreate() {
  isEditing.value = false;
  editingId.value = null;
  modalForm.name = "";
  modalForm.display_order = categorias.value.length + 1;
  modalError.value = "";
  modalOpen.value = true;
}

function openModalEdit(cat) {
  isEditing.value = true;
  editingId.value = cat.id;
  modalForm.name = cat.name || "";
  modalForm.display_order = cat.display_order ?? 0;
  modalError.value = "";
  modalOpen.value = true;
}

async function onSave() {
  modalSaving.value = true;
  modalError.value = "";
  try {
    if (isEditing.value) {
      await editarCategoriaCatalogo(editingId.value, {
        name: modalForm.name,
        display_order: modalForm.display_order,
      });
    } else {
      await crearCategoriaCatalogo({
        name: modalForm.name,
        display_order: modalForm.display_order,
      });
    }
    modalOpen.value = false;
    await loadData();
  } catch (err) {
    modalError.value =
      err.response?.data?.message || "Error al guardar la categoría.";
  } finally {
    modalSaving.value = false;
  }
}

async function onDelete(cat) {
  const confirmed = await confirm({
    title: "Eliminar categoría",
    message: `¿Eliminar la categoría "${cat.name}"?`,
  });
  if (!confirmed) return;
  try {
    await eliminarCategoriaCatalogo(cat.id);
    await loadData();
  } catch (err) {
    error.value = err.response?.data?.message || "Error al eliminar la categoría.";
  }
}

onMounted(loadData);
</script>
